import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import { X, Check, Plus, Trash2, GitBranch, Save, Navigation, MapPin, Edit, Settings } from 'lucide-react';
import L from 'leaflet';

// Configurar ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-shadow.png',
});

// Componente para capturar cliques no mapa
function MapClickHandler({ onMapClick, ativo }) {
  const map = useMap();

  useEffect(() => {
    if (!ativo) return;

    const handleClick = (e) => {
      onMapClick([e.latlng.lat, e.latlng.lng]);
    };

    map.on('click', handleClick);
    return () => map.off('click', handleClick);
  }, [map, onMapClick, ativo]);

  return null;
}

const RoteadorInteligente = ({ onTrechosAtualizados, onFechar, titulo, trechosIniciais = [] }) => {
  const [trechos, setTrechos] = useState(trechosIniciais);
  const [trechoAtivo, setTrechoAtivo] = useState(null);
  const [modoEdicao, setModoEdicao] = useState('visualizar');
  const [pontosTemporarios, setPontosTemporarios] = useState([]);
  const [rotaCalculada, setRotaCalculada] = useState([]);
  const [nomeNovoTrecho, setNomeNovoTrecho] = useState('');
  const [nomeNovaDerivacao, setNomeNovaDerivacao] = useState('');
  const [carregandoRota, setCarregandoRota] = useState(false);
  const [modoRoteamento, setModoRoteamento] = useState('automatico'); // 'automatico', 'manual', 'edicao'
  const [trechoEditando, setTrechoEditando] = useState(null);
  const [pontosEditaveis, setPontosEditaveis] = useState([]);

  // Coordenadas de Conceição do Castelo - ES
  const centroConceicaoCastelo = [-20.359292, -41.247282];

  // Função para calcular rota usando OpenRouteService (gratuito)
  const calcularRota = async (pontos) => {
    if (pontos.length < 2) return pontos;

    setCarregandoRota(true);
    try {
      // Usando OSRM (Open Source Routing Machine) - gratuito
      const coordenadas = pontos.map(p => `${p[1]},${p[0]}`).join(';');
      const url = `https://router.project-osrm.org/route/v1/driving/${coordenadas}?overview=full&geometries=geojson`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.routes && data.routes[0]) {
        const geometry = data.routes[0].geometry;
        // Converter coordenadas GeoJSON para formato Leaflet [lat, lng]
        const rotaPontos = geometry.coordinates.map(coord => [coord[1], coord[0]]);
        return rotaPontos;
      }
    } catch (error) {
      console.warn('Erro ao calcular rota automática, usando pontos diretos:', error);
    } finally {
      setCarregandoRota(false);
    }

    // Se falhar, retorna os pontos originais
    return pontos;
  };

  const handleMapClick = async (coordenadas) => {
    if (modoEdicao === 'novo-trecho') {
      const novosPontos = [...pontosTemporarios, coordenadas];
      setPontosTemporarios(novosPontos);

      // Se modo automático e temos pelo menos 2 pontos, calcular rota
      if (modoRoteamento === 'automatico' && novosPontos.length >= 2) {
        const rota = await calcularRota(novosPontos);
        setRotaCalculada(rota);
      } else if (modoRoteamento === 'manual') {
        // No modo manual, usar pontos diretos
        setRotaCalculada(novosPontos);
      }
    } else if (modoEdicao === 'nova-derivacao' && trechoAtivo) {
      const novosPontos = [...pontosTemporarios, coordenadas];
      setPontosTemporarios(novosPontos);

      if (modoRoteamento === 'automatico' && novosPontos.length >= 2) {
        const rota = await calcularRota(novosPontos);
        setRotaCalculada(rota);
      } else if (modoRoteamento === 'manual') {
        setRotaCalculada(novosPontos);
      }
    } else if (modoEdicao === 'editar-trecho') {
      // Adicionar ponto à rota em edição
      const novosPontos = [...pontosEditaveis, coordenadas];
      setPontosEditaveis(novosPontos);
    }
  };

  const iniciarNovoTrecho = () => {
    setModoEdicao('novo-trecho');
    setNomeNovoTrecho(`Trecho ${trechos.length + 1}`);
    setPontosTemporarios([]);
    setRotaCalculada([]);
  };

  const iniciarNovaDerivacao = (trecho) => {
    setTrechoAtivo(trecho);
    setModoEdicao('nova-derivacao');
    setNomeNovaDerivacao(`Derivação ${(trecho.derivacoes?.length || 0) + 1}`);
    setPontosTemporarios([]);
    setRotaCalculada([]);
  };

  const confirmarTrecho = async () => {
    if (!nomeNovoTrecho.trim() || pontosTemporarios.length < 2) {
      alert('É necessário um nome e pelo menos 2 pontos para criar o trecho');
      return;
    }

    const rotaFinal = rotaCalculada.length > 0 ? rotaCalculada : pontosTemporarios;

    const novoTrecho = {
      id: Date.now(),
      nome: nomeNovoTrecho.trim(),
      tipo: 'principal',
      pontos: rotaFinal,
      pontosOriginais: pontosTemporarios, // Guardar pontos clicados pelo usuário
      derivacoes: []
    };

    setTrechos(prev => [...prev, novoTrecho]);
    cancelarEdicao();
  };

  const confirmarDerivacao = async () => {
    if (!nomeNovaDerivacao.trim() || pontosTemporarios.length < 2) {
      alert('É necessário um nome e pelo menos 2 pontos para criar a derivação');
      return;
    }

    const rotaFinal = rotaCalculada.length > 0 ? rotaCalculada : pontosTemporarios;

    const novaDerivacao = {
      id: Date.now(),
      nome: nomeNovaDerivacao.trim(),
      pontos: rotaFinal,
      pontosOriginais: pontosTemporarios
    };

    setTrechos(prev => prev.map(trecho =>
      trecho.id === trechoAtivo.id
        ? { ...trecho, derivacoes: [...(trecho.derivacoes || []), novaDerivacao] }
        : trecho
    ));

    cancelarEdicao();
  };

  const cancelarEdicao = () => {
    setModoEdicao('visualizar');
    setTrechoAtivo(null);
    setPontosTemporarios([]);
    setRotaCalculada([]);
    setNomeNovoTrecho('');
    setNomeNovaDerivacao('');
  };

  const excluirTrecho = (trechoId) => {
    if (window.confirm('Tem certeza que deseja excluir este trecho e todas suas derivações?')) {
      setTrechos(prev => prev.filter(t => t.id !== trechoId));
    }
  };

  const excluirDerivacao = (trechoId, derivacaoId) => {
    if (window.confirm('Tem certeza que deseja excluir esta derivação?')) {
      setTrechos(prev => prev.map(trecho =>
        trecho.id === trechoId
          ? { ...trecho, derivacoes: trecho.derivacoes.filter(d => d.id !== derivacaoId) }
          : trecho
      ));
    }
  };

  const desfazerUltimoPonto = () => {
    if (pontosTemporarios.length > 0) {
      const novosPontos = pontosTemporarios.slice(0, -1);
      setPontosTemporarios(novosPontos);

      if (novosPontos.length >= 2) {
        calcularRota(novosPontos).then(setRotaCalculada);
      } else {
        setRotaCalculada([]);
      }
    }
  };

  const confirmarTrechos = () => {
    if (trechos.length === 0) {
      alert('É necessário adicionar pelo menos um trecho');
      return;
    }
    onTrechosAtualizados(trechos);
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '1200px', width: '95%', height: '90vh' }}>
        <div className="modal-header">
          <h3 className="modal-title">{titulo}</h3>
          <button className="close-button" onClick={onFechar}>
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', height: 'calc(100% - 80px)', gap: '20px' }}>
          {/* Painel lateral de controle */}
          <div style={{ width: '320px', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '15px', overflowY: 'auto' }}>

            {/* Instruções */}
            <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '6px' }}>
              <h5 style={{ margin: '0 0 8px 0', color: '#1976d2' }}>
                <Navigation size={16} style={{ marginRight: '6px' }} />
                Roteamento Inteligente
              </h5>
              <p style={{ margin: 0, fontSize: '12px', color: '#1565c0' }}>
                Clique nos pontos do mapa e o sistema calculará automaticamente a rota seguindo as ruas reais.
              </p>
            </div>

            {/* Controles principais */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '10px' }}>Controles</h4>

              {modoEdicao === 'visualizar' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button className="button" onClick={iniciarNovoTrecho}>
                    <Plus size={16} />
                    Novo Trecho
                  </button>
                </div>
              )}

              {modoEdicao === 'novo-trecho' && (
                <div>
                  <div className="form-group" style={{ marginBottom: '10px' }}>
                    <label>Nome do Trecho:</label>
                    <input
                      type="text"
                      value={nomeNovoTrecho}
                      onChange={(e) => setNomeNovoTrecho(e.target.value)}
                      placeholder="Ex: Rua Principal"
                    />
                  </div>

                  <div style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#856404' }}>
                      <strong>Pontos marcados:</strong> {pontosTemporarios.length}
                      {carregandoRota && <span> • Calculando rota...</span>}
                    </p>
                    {rotaCalculada.length > 0 && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#155724' }}>
                        <MapPin size={12} style={{ marginRight: '4px' }} />
                        Rota calculada: {rotaCalculada.length} pontos
                      </p>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      className="button secondary"
                      onClick={desfazerUltimoPonto}
                      disabled={pontosTemporarios.length === 0}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      Desfazer
                    </button>
                    <button
                      className="button"
                      onClick={confirmarTrecho}
                      disabled={pontosTemporarios.length < 2}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      <Check size={12} />
                      Confirmar
                    </button>
                    <button
                      className="button secondary"
                      onClick={cancelarEdicao}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {modoEdicao === 'nova-derivacao' && (
                <div>
                  <div className="form-group" style={{ marginBottom: '10px' }}>
                    <label>Nome da Derivação:</label>
                    <input
                      type="text"
                      value={nomeNovaDerivacao}
                      onChange={(e) => setNomeNovaDerivacao(e.target.value)}
                      placeholder="Ex: Acesso à Escola"
                    />
                  </div>

                  <div style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#d4edda', borderRadius: '4px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#155724' }}>
                      <strong>Derivação de:</strong> {trechoAtivo?.nome}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#155724' }}>
                      <strong>Pontos marcados:</strong> {pontosTemporarios.length}
                      {carregandoRota && <span> • Calculando rota...</span>}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      className="button secondary"
                      onClick={desfazerUltimoPonto}
                      disabled={pontosTemporarios.length === 0}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      Desfazer
                    </button>
                    <button
                      className="button"
                      onClick={confirmarDerivacao}
                      disabled={pontosTemporarios.length < 2}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      <Check size={12} />
                      Confirmar
                    </button>
                    <button
                      className="button secondary"
                      onClick={cancelarEdicao}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Lista de trechos */}
            <div>
              <h4 style={{ marginBottom: '10px' }}>Trechos ({trechos.length})</h4>

              {trechos.length === 0 ? (
                <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', padding: '20px' }}>
                  Nenhum trecho adicionado
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {trechos.map(trecho => (
                    <div key={trecho.id} style={{
                      padding: '10px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: '1px solid #dee2e6'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '14px' }}>{trecho.nome}</strong>
                        <button
                          className="button danger"
                          onClick={() => excluirTrecho(trecho.id)}
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>

                      <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                        {trecho.pontos.length} pontos na rota
                        {trecho.pontosOriginais && (
                          <span> • {trecho.pontosOriginais.length} pontos marcados</span>
                        )}
                      </p>

                      <button
                        className="button"
                        onClick={() => iniciarNovaDerivacao(trecho)}
                        style={{ padding: '4px 8px', fontSize: '12px', width: '100%' }}
                        disabled={modoEdicao !== 'visualizar'}
                      >
                        <GitBranch size={12} />
                        Adicionar Derivação
                      </button>

                      {/* Lista de derivações */}
                      {trecho.derivacoes && trecho.derivacoes.length > 0 && (
                        <div style={{ marginTop: '8px', paddingLeft: '10px' }}>
                          <p style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                            Derivações:
                          </p>
                          {trecho.derivacoes.map(derivacao => (
                            <div key={derivacao.id} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 8px',
                              backgroundColor: '#e9ecef',
                              borderRadius: '4px',
                              marginBottom: '4px'
                            }}>
                              <span style={{ fontSize: '12px' }}>
                                {derivacao.nome} ({derivacao.pontos.length} pts)
                              </span>
                              <button
                                className="button danger"
                                onClick={() => excluirDerivacao(trecho.id, derivacao.id)}
                                style={{ padding: '2px 6px', fontSize: '10px' }}
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mapa */}
          <div style={{ flex: 1 }}>
            <MapContainer
              center={centroConceicaoCastelo}
              zoom={14}
              style={{ height: '100%', width: '100%', borderRadius: '8px' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              <MapClickHandler
                onMapClick={handleMapClick}
                ativo={modoEdicao === 'novo-trecho' || modoEdicao === 'nova-derivacao'}
              />

              {/* Renderizar trechos existentes */}
              {trechos.map(trecho => (
                <React.Fragment key={trecho.id}>
                  {/* Trecho principal */}
                  <Polyline
                    positions={trecho.pontos}
                    color="#007bff"
                    weight={4}
                    opacity={0.8}
                  />

                  {/* Pontos originais marcados pelo usuário */}
                  {trecho.pontosOriginais && trecho.pontosOriginais.map((ponto, index) => (
                    <Marker key={`orig-${index}`} position={ponto}>
                      <Popup>
                        <div>
                          <strong>{trecho.nome}</strong><br />
                          Ponto {index + 1} marcado pelo usuário
                        </div>
                      </Popup>
                    </Marker>
                  ))}

                  {/* Derivações */}
                  {trecho.derivacoes && trecho.derivacoes.map(derivacao => (
                    <Polyline
                      key={derivacao.id}
                      positions={derivacao.pontos}
                      color="#28a745"
                      weight={3}
                      opacity={0.7}
                      dashArray="5, 5"
                    />
                  ))}
                </React.Fragment>
              ))}

              {/* Pontos temporários sendo marcados */}
              {pontosTemporarios.map((ponto, index) => (
                <Marker key={`temp-${index}`} position={ponto}>
                  <Popup>
                    <div>
                      Ponto {index + 1}
                      {index === 0 && <span><br />Início</span>}
                      {index === pontosTemporarios.length - 1 && index > 0 && <span><br />Último ponto</span>}
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Rota calculada temporária */}
              {rotaCalculada.length > 0 && (
                <Polyline
                  positions={rotaCalculada}
                  color={modoEdicao === 'novo-trecho' ? '#007bff' : '#28a745'}
                  weight={modoEdicao === 'novo-trecho' ? 4 : 3}
                  opacity={0.6}
                  dashArray={modoEdicao === 'nova-derivacao' ? '5, 5' : null}
                />
              )}
            </MapContainer>
          </div>
        </div>

        {/* Botões de ação */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '15px' }}>
          <button className="button secondary" onClick={onFechar}>
            Cancelar
          </button>
          <button
            className="button"
            onClick={confirmarTrechos}
            disabled={trechos.length === 0}
          >
            <Save size={16} />
            Salvar Trechos ({trechos.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoteadorInteligente;